import type { JSX } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  Button,
  Dialog,
  Flex,
  Heading,
  Image,
  Paragraph,
} from '@buffer-mono/popcorn'

import {
  connectChannel,
  isChannelConnectionAvailable,
  toConnectableService,
} from '~publish/helpers/channelConnections'
import { actions as modalActions } from '~publish/legacy/modals/reducer'
import { settingsChannelsRoute } from '~publish/legacy/routes'
import { useTracking } from '~publish/legacy/tracking/useTracking'

import type { ServiceInfo } from './configuration'
import { connectAChannelServiceData } from './configuration'
import { getCTAValue, getUpgradePathName } from './utils'

import styles from './ConnectAChannelModal.module.css'

export const getServiceData = (serviceId: string): ServiceInfo | undefined => {
  return connectAChannelServiceData.find((serv) => serv.id === serviceId)
}

const ConnectAChannelModal = ({
  service,
}: {
  service: string
}): JSX.Element | null => {
  const [bannerState, setBannerState] = useState<
    'loading' | 'loaded' | 'failed'
  >('loading')
  const dispatch = useDispatch()
  const serviceData = getServiceData(service)

  const onClose = (): void => {
    dispatch(modalActions.hideConnectAChannelModal())
  }

  /* Preloaded so the banner is only mounted once it can paint. The dialog
   * itself never waits on it: a banner that 404s or simply never resolves
   * must not keep the connect button off screen. */
  useEffect(() => {
    if (!serviceData?.banner_img_url) return

    const img = new window.Image()
    const handleImageLoad = (): void => setBannerState('loaded')
    const handleImageError = (): void => setBannerState('failed')
    img.addEventListener('load', handleImageLoad)
    img.addEventListener('error', handleImageError)
    img.src = serviceData.banner_img_url

    return (): void => {
      img.removeEventListener('load', handleImageLoad)
      img.removeEventListener('error', handleImageError)
    }
  }, [serviceData?.banner_img_url])

  if (!serviceData) return null

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <Dialog.Content size="medium" className={styles.content}>
        <Flex direction="column" align="center" gap="lg">
          {bannerState === 'loaded' && (
            <Image
              alt={`${serviceData.label} banner`}
              className={styles.banner}
              src={serviceData.banner_img_url}
            />
          )}
          <Flex direction="column" align="center" gap="xs">
            <Heading size="medium" align="center" color="neutral">
              {serviceData.title}
            </Heading>
            <Paragraph align="center" color="subtle">
              {serviceData.description}
            </Paragraph>
          </Flex>
          <ConnectAChannelModalFooter
            serviceData={serviceData}
            onClose={onClose}
          />
        </Flex>
      </Dialog.Content>
    </Dialog>
  )
}

/*
 * The footer contains two buttons:
 *  - Connect to the selected channel => starts the proces to connect
 *  - Connect Other channels => goes to channel list page
 */

const ConnectAChannelModalFooter = ({
  serviceData,
  onClose,
}: {
  serviceData: ServiceInfo
  onClose: () => void
}): JSX.Element => {
  const [processing, setProcessing] = useState(false)
  const navigate = useNavigate()
  const { trackCTAClicked, trackChannelCTAViewed, trackChannelCTAClicked } =
    useTracking()
  const trackChannelCTAViewedRef = useRef(trackChannelCTAViewed)
  trackChannelCTAViewedRef.current = trackChannelCTAViewed

  const payloadChannelCTA = useMemo(
    () => ({
      cta: getCTAValue(serviceData.id, 'connectButton'),
      upgradePathName: getUpgradePathName(serviceData.id),
      channel: serviceData.id,
    }),
    [serviceData.id],
  )

  const payloadOtherChannelsCTA = {
    cta: getCTAValue(serviceData.id, 'connectOtherChannels'),
    upgradePathName: getUpgradePathName(serviceData.id),
  }

  useEffect(() => {
    trackChannelCTAViewedRef.current(payloadChannelCTA)
  }, [payloadChannelCTA])
  /*
   * Takes the user to the Channels list page
   */
  const goToChannelsPage = (): void => {
    trackCTAClicked(payloadOtherChannelsCTA)
    onClose()
    navigate(settingsChannelsRoute.route)
  }

  /*
   * Starts the process and closes the modal
   */
  const startConnection = (): void => {
    const service = toConnectableService(serviceData.id)

    if (service) {
      connectChannel({ service })
    }

    setProcessing(false)
    onClose()
  }

  /*
   * If appshell is available, call connectChannel method
   * in other case, wait for appshell to be loaded
   */
  const handleOnClick = (): void => {
    trackChannelCTAClicked(payloadChannelCTA)

    /* we disabled the button to avoid multiple clicks
    while we wait for window.apshell  */
    setProcessing(true)

    if (!isChannelConnectionAvailable()) {
      window.addEventListener('load', startConnection)
      return
    }

    window.removeEventListener('load', startConnection)
    startConnection()
  }

  return (
    <Flex direction="column" gap="xs" align="center">
      <Button
        aria-label={serviceData.buttonLabel}
        data-testid="connect-channel-modal-connect-button"
        variant="primary"
        className={styles.button}
        size="large"
        onClick={handleOnClick}
        disabled={processing}
      >
        {serviceData.icon}
        {processing ? 'Processing...' : serviceData.buttonLabel}
      </Button>

      <Button
        arial-label="Connect Other channels"
        data-testid="connect-channel-modal-connect-other-button"
        variant="tertiary"
        className={styles.button}
        size="large"
        onClick={goToChannelsPage}
      >
        Connect Other Channels
      </Button>
    </Flex>
  )
}

export { ConnectAChannelModal }
